import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useT } from '../i18n/locale-context';
import { deleteSavedSearch } from '../lib/saved-search-client';

/**
 * Deleting a saved search — the confirmation and the mutation, in ONE place.
 *
 * Two surfaces ask for it: the sidebar's ✕ and the «Сохранённые поиски» card on
 * /app/alerts. They differ in shape (an inline row swap in a 250px rail; a
 * dialog on a settings page) and deliberately stay that way — a modal over the
 * sidebar is heavier than the act deserves, which is the reasoning
 * `SavedSearchList` already carries. What must NOT differ is what the user is
 * told and what happens after: an alert stops arriving SILENTLY, so the same
 * sentence has to name the search and say the notifications end.
 *
 * ⚠️ The task this came from said the sidebar «✕» deletes without asking.
 * Verified against the code: it does ask — a two-step inline «Удалить? · Да ·
 * Отмена» has been there since the pencil landed. What it did NOT do is name
 * the search or say what stops. That is what this unifies.
 */

/** The one sentence both surfaces show. */
export function useSavedSearchDeletePrompt(): (name: string) => string {
  const t = useT();
  return (name: string) => t('savedSearches.delete.confirm', { name });
}

/**
 * The delete mutation + cache invalidation, shared so the two callers cannot
 * drift on which queries they refresh. `['saved-searches']` feeds the sidebar,
 * the alerts page AND «Мои тендеры»; `['my-tenders']` holds pages whose «по
 * поиску» labels reference the search that just went away.
 */
export function useDeleteSavedSearch(onDone?: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteSavedSearch(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['saved-searches'] });
      // Prefix invalidation: every locale/chip/page combination is stale, and
      // a page still labelled «по поиску: <deleted>» is the visible symptom.
      await qc.invalidateQueries({ queryKey: ['my-tenders'] });
      // The /results search is deleted through the same route (by id).
      await qc.invalidateQueries({ queryKey: ['results-search'] });
      onDone?.();
    },
  });
}
